import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { ApplicationState } from '../../../store';
import { Dictionary, EntityType } from "../../../entities/common";
import { DetailsSpinner } from "../../common/Spinner";
import { actionCreators as CalendarsActionCreators } from '../../../store/CalendarsListStore';
import { nameof, namesof } from '../../../store/services/metadataService';
import { IControlConfiguration } from '../../common/interfaces/ISectionUIControlProps';
import { IFieldsAreaConfiguration } from '../../common/sectionsControl/uiControls/fieldsArea/common';
import * as Metadata from '../../../entities/Metadata';
import { IInputProps } from '../../common/interfaces/IInputProps';
import { Calendar, CalendarStatus, ICalendarAttrs, getUserCalendar, isUserCalendarId } from '../../../store/calendar/common';
import CalendarHeader, { removeDraftOptionIfEntitiesExist } from './CalendarHeader';
import { DisplayFieldService } from '../../common/DisplayFieldService';
import CalendarSectionsContainer from './CalendarSectionsContainer';
import * as UserStore from '../../../store/User';
import * as ResourcesListStore from '../../../store/ResourcesListStore';

interface IConfiguration extends Dictionary<IControlConfiguration> {
    FieldsArea: IFieldsAreaConfiguration;
}

type StateProps = {
    entity?: Calendar;
    user: UserStore.UserState;
    isLoading: boolean;
    isUserCalendar: boolean;
};

type ActionProps = {
    calendarsActions: typeof CalendarsActionCreators;
    userActions: typeof UserStore.actionCreators;
    resourcesActions: typeof ResourcesListStore.actionCreators;
};

type Props = RouteComponentProps<{ id: string }> & StateProps & ActionProps;

const CalendarDetails = (props: Props) => {
    const { entity, isUserCalendar } = props;

    React.useEffect(() => {
        if (UserStore.isAdminViewer(props.user) && isUserCalendar) {
            props.history.replace('/calendars');
            return;
        }
        props.userActions.loadResource();
    }, []);

    React.useEffect(() => {
        if (!isUserCalendar) {
            props.calendarsActions.loadCalendar(props.match.params.id);
        }
    }, [props.match.params.id]);

    React.useEffect(() => {
        if (isUserCalendar && props.user.resource?.id) {
            props.resourcesActions.loadResource(props.user.resource?.id, true);
        }
    }, [props.match.params.id, props.user.resource?.id]);

    const cloneCalendar = () => {
        props.calendarsActions.cloneCalendar(entity!.id);
    };

    const removeCalendar = () => {
        props.calendarsActions.removeCalendars([entity!.id], true);
    };

    const updateCalendarStatus = (status: CalendarStatus) => {
        props.calendarsActions.updateAttributes(entity!.id, { [nameof<ICalendarAttrs>('Status')]: status });
    };

    const makeCalendarDefault = () => {
        props.calendarsActions.makeCalendarDefault(entity!.id);
    };

    const refreshEntity = () => {
        if (!isUserCalendar) {
            props.calendarsActions.loadCalendar(props.match.params.id);
        }
    };

    const onEditComplete = (fieldName: string, fieldValue: any, extra: Dictionary<any>): void => {
        if (!fieldName) {
            return;
        }
        props.calendarsActions.updateAttributes(entity!.id, { [fieldName]: fieldValue, ...extra });
    };

    const disabledReadOnlyFields = React.useMemo(() => {
        let fields = [];
        if (entity?.isDefault) {
            fields.push(nameof<ICalendarAttrs>('Status'));
        }
        if (entity?.isUserCalendar) {
            fields = [...fields, ...namesof<ICalendarAttrs>(['Name', 'Status', 'Description'])];
        }

        return fields;
    }, [entity, props.user.id]);

    const buildControlsConfigurations = (): IConfiguration => {
        return {
            ['FieldsArea']: {
                datacontext: {
                    entityId: entity!.id,
                    entityType: EntityType.Calendar,
                    readOnlyFields: disabledReadOnlyFields,
                    disabledFields: disabledReadOnlyFields,
                },
                actions: {
                    refreshEntity,
                    onEditComplete,
                    updateUIControl: null,
                },
                editProps: {
                    elementCustomRender: {
                        [nameof<ICalendarAttrs>("Status")]: (inputProps: IInputProps, state: Calendar, field: Metadata.Field): JSX.Element | null => {
                            const updatedField = removeDraftOptionIfEntitiesExist(field, state);
                            return DisplayFieldService.buildFieldInputElement(updatedField, inputProps);
                        }
                    }
                },
            },
        };
    }

    return (<DetailsSpinner isLoading={props.isLoading}>
        {!!entity && <>
            <CalendarHeader
                entity={entity}
                actions={{ cloneCalendar, removeCalendar, updateCalendarStatus, makeCalendarDefault }}
            />
            <CalendarSectionsContainer
                calendar={entity}
                controlsConfig={buildControlsConfigurations()}
            />
        </>}
    </DetailsSpinner>);
};

function mergeActionCreators(dispatch: any): ActionProps {
    return {
        calendarsActions: bindActionCreators(CalendarsActionCreators, dispatch),
        userActions: bindActionCreators(UserStore.actionCreators, dispatch),
        resourcesActions: bindActionCreators(ResourcesListStore.actionCreators, dispatch),
    };
}

function mapStateToProps(state: ApplicationState, ownProps: RouteComponentProps<{ id: string }>): StateProps {
    const isUserCalendar = isUserCalendarId(ownProps.match.params.id, state.user);
    return {
        entity:  isUserCalendar ? getUserCalendar(state) : state.calendars.activeEntity,
        isLoading: isUserCalendar ? false : state.calendars.isLoading || state.calendars.isListLoading,
        user: state.user,
        isUserCalendar,
    };
}

export default connect(mapStateToProps, mergeActionCreators)(CalendarDetails);
